import mapKeys from 'lodash/mapKeys'
import omit from 'lodash/omit'
import { defineMessages } from 'react-intl'
import { v4 } from 'uuid'

import config from '@app/config'

import { MISC_MESSAGES } from '@app/constants/Messages/misc'

import { AbortError } from '@app/errors/AbortError'

import { getRouter } from '@app/utils/routing/Context'
import { read as readSource } from '@app/utils/sourceRecorder/read'

import type { TrackingEvent } from '@app/packages/api/v2/types/params'
import { createAddress } from '@app/packages/geo/Address'
import { MAP_AREA_KIND } from '@app/packages/geo/constants'
import type { MapBounds } from '@app/packages/geo/MapBounds'

import { type CloudPaymentsRequest } from '@app/hooks/useCloudPayments'

import { configSelector, setConfigSlice } from '@app/store/slices/config'
import { intlSelector } from '@app/store/slices/intl'
import { createThunk } from '@app/store/thunk'
import { type ConfigState } from '@app/store/types/config'

import { callV2Api } from './api'
import { getCookies } from './contexts/cookies'

export const setConfig = (partialConfig: Partial<ConfigState>) => {
  return createThunk(dispatch => {
    dispatch(setConfigSlice(partialConfig))
    Object.assign(config, partialConfig)
  })
}

export function postTrackings(event: TrackingEvent) {
  return createThunk(async (dispatch, getState) => {
    const cookies = dispatch(getCookies())
    // `referer` is the recorder's own note of where an attribution came from, not the event's referrer.
    const source = mapKeys(omit(await readSource(cookies), 'referer'), (_value, key) => {
      if (key.indexOf('utm_') === 0) return key.replace(/^utm_/, '')
      return key
    })
    // The app's WebView opens without utm or a referer, so name the app rather than send nothing.
    const platform = configSelector(getState()).applicationAgentData?.platform
    if (!source.source && platform) source.source = `app_${platform}`

    const { postTrackings: postTrackingsAction } = await import('@app/packages/api/v2/actions/trackings')
    const { location } = dispatch(getRouter())
    const referrer = `${configSelector(getState()).origin}${location.pathname}${location.search ?? ''}`

    return dispatch(callV2Api(postTrackingsAction({ event_id: v4(), event_type: event.type, event_data: event.data, referrer }, source)))
  })
}

export class CloudPaymentsChargeCancelledError extends AbortError {}

export function cloudPaymentsCharge(request: Omit<CloudPaymentsRequest, 'publicId' | 'skin'>) {
  return createThunk(async (_dispatch, getState) => {
    const { loadCloudPayments } = await import('@app/hooks/useCloudPayments')
    const cloudPayments = await loadCloudPayments()
    const cloudPaymentsId = configSelector(getState()).cloudpayments

    const widget = new cloudPayments.CloudPayments()

    return await new Promise<void>((resolve, reject) => {
      widget.pay(
        'charge',
        {
          ...request,
          publicId: cloudPaymentsId,
          skin: 'modern',
        },
        {
          onSuccess: () => {
            resolve()
          },
          onFail: c => {
            if (c === 'User has cancelled') {
              reject(new CloudPaymentsChargeCancelledError(c))
            } else {
              reject(new Error(c))
            }
          },
        }
      )
    })
  })
}

export function getAddressFromBounds(bounds: MapBounds) {
  return createThunk((_dispatch, getState) => {
    const { formatMessage } = intlSelector(getState())

    return createAddress({
      // Outside production the area reads as its latitudes / longitudes, to three digits: about 100m, enough to tell areas apart.
      label: API_MODE !== 'production' ? boundsLabel(bounds) : formatMessage(messages.map_area),
      kind: MAP_AREA_KIND,
      location: {
        lat: (bounds[0][0] + bounds[1][0]) / 2,
        lon: (bounds[0][1] + bounds[1][1]) / 2,
      },
      bounds: {
        bottomLeft: { lat: bounds[0][0], lon: bounds[0][1] },
        topRight: { lat: bounds[1][0], lon: bounds[1][1] },
      },
    })
  })
}

export function formatDistance(
  /** Distance in meters */
  distance: number
) {
  return createThunk((_dispatch, getState) => {
    const { formatMessage } = intlSelector(getState())
    const km = Math.floor(distance / 1000)
    const m = distance % 1000
    return [km ? formatMessage(MISC_MESSAGES.km, { distance: km }) : null, m || !km ? formatMessage(MISC_MESSAGES.m, { distance: m }) : null]
      .filter(Boolean)
      .join(' ')
  })
}

const messages = defineMessages({
  map_area: { id: "store.actions.misc.messages.map_area.67ec", defaultMessage: 'Участок карты' },
})

const boundsLabel = ([[south, west], [north, east]]: MapBounds) => `${south.toFixed(3)}-${north.toFixed(3)}/${west.toFixed(3)}-${east.toFixed(3)}`
